<template>
  <div>
    <AccountTable
      ref="licenseTable"
      display-application-name
      :filter="filter"
      display-status-indicator
      :custom-columns="[actionColumn, statusColumn]"
      :display-actions="false"
      :select="props.selectable"
      :click-action="() => {}"
      :display-last-logged-in="false"
      :display-email="false"
      :contain-strict="false">
      <!-- Licence Action (delete, deactivate) -->
      <template #licence_action="{ row }">
        <!-- Show Dropdown if supports delete and disable -->
        <SmDropdown
          v-if="
            row.software_id in applicationInformation &&
            applicationInformation[row.software_id]
              .implements_delete_user_method &&
            applicationInformation[row.software_id]
              .implements_disable_licence_method
          "
          @input="(value) => (manualChangedActions[row._id!] = value)">
          <SmDropdownItem
            v-if="
              applicationInformation[row.software_id]
                .implements_delete_user_method
            "
            @click="manualChangedActions[row._id!] = 'delete'">
            {{ i18n.t('delete') }}
          </SmDropdownItem>

          <SmDropdownItem
            v-if="
              applicationInformation[row.software_id]
                .implements_disable_licence_method
            "
            @click="manualChangedActions[row._id!] = 'deactivate'">
            {{ i18n.t('deactivate') }}
          </SmDropdownItem>

          <template #trigger>
            <div class="flex items-center gap-2">
              {{ i18n.t(currentActions[row._id!]) }}
              <ChevronDown :size="18" />
            </div>
          </template>
        </SmDropdown>

        <!-- Else show text -->
        <template v-else>
          <span v-if="currentActions">
            {{ i18n.t(currentActions[row._id!]) }}
          </span>
        </template>
      </template>

      <!-- Staus Column -->
      <template #deletion_status="{ row }">
        <RotateCcw v-if="deletePeopleLoading" :size="20" />

        <CircleCheck
          v-else-if="results[row._id!]?.status == 'fulfilled'"
          :size="20" />

        <CircleX
          v-else-if="results[row._id!]?.status == 'rejected'"
          :size="20" />

        <div v-else />
      </template>
    </AccountTable>
  </div>
</template>

<script lang="ts" setup>
  import { ComponentExposed } from 'vue-component-type-helpers'
  import { useI18n } from 'vue-i18n'

  import {
    Account,
    LicenceOut,
    SoftwareInformation,
    SoftwareOut,
    UserLicencesService,
  } from '@/client'
  import { ApplicationStore } from '@/stores/application/application.store'
  import { AccountFilters } from '@/stores/account/account.filter'
  import { PersonStore } from '@/stores/person/person.store'
  import { ChevronDown, CircleCheck, CircleX, RotateCcw } from 'lucide-vue-next'
  import LicenseTable from '@/components/accounts/AccountTable.vue'

  const i18n = useI18n()

  const licenseTable = ref<ComponentExposed<typeof LicenseTable>>()

  // Hooks
  onMounted(async () => {
    application.value = await ApplicationStore.getApplications<SoftwareOut>()
  })

  const emit = defineEmits(['person-deleted', 'license-deleted'])

  const props = defineProps({
    person: {
      type: Object as PropType<Account>,
      required: true,
    },
    selectable: {
      type: Boolean,
      default: true,
    },
  })

  const statusColumn = {
    key: 'deletion_status',
    label: '',
    sortable: false,
    width: '30px',
  }
  const actionColumn = {
    key: 'licence_action',
    label: i18n.t('general.action'),
    sortable: false,
    width: '130px',
  }

  // Deletion
  const deletePeopleLoading = ref(false)
  const personDeleted = ref(false)

  const results: Ref<Record<string, PromiseSettledResult<unknown>>> = ref({})

  async function deleteLicenses() {
    const licensesToDelete: LicenceOut[] = licenseTable.value?.selection || []
    const promises: Promise<unknown>[] = []

    licensesToDelete.forEach((license: LicenceOut) => {
      if (currentActions.value[license._id!] === 'delete') {
        promises.push(
          UserLicencesService.deleteApiV1SoftwareLicencesLicenceIdDelete({
            licenceId: license._id!,
          })
        )
      } else if (currentActions.value[license._id!] === 'deactivate') {
        promises.push(
          UserLicencesService.disableLicenceApiV1SoftwareLicencesLicenceIdDisablePost(
            {
              licenceId: license._id!,
            }
          )
        )
      }
    })

    deletePeopleLoading.value = true

    const result: PromiseSettledResult<unknown>[] =
      await Promise.allSettled(promises)

    // Map results to license ids
    result.forEach((res: PromiseSettledResult<unknown>, index: number) => {
      results.value[licensesToDelete[index]._id!] = res
    })

    deletePeopleLoading.value = false

    return true
  }

  function createRequestBody() {
    return currentActions.value
  }

  async function excecute() {
    const allLicenseDeletionsSuccessful = await deleteLicenses()

    if (!allLicenseDeletionsSuccessful) {
      return false
    }

    // Delete User
    if (allLicensesSelected.value) {
      PersonStore.deletePerson(props.person._id).then(() => {
        emit('person-deleted', props.person)
      })
    }

    finishDeletion()

    return true
  }

  function finishDeletion() {
    deletePeopleLoading.value = false
    personDeleted.value = true

    emit('license-deleted')
  }

  //  Software
  const application = ref<SoftwareOut[]>()

  const applicationInformation: ComputedRef<
    Record<string, SoftwareInformation>
  > = computed(() => {
    const appInformation: Record<string, SoftwareInformation> = {}

    application.value?.forEach((app) => {
      appInformation[app._id] = app.information
    })

    return appInformation
  })

  // Actions
  type PossibleActions = 'delete' | 'deactivate' | 'none'
  const manualChangedActions = ref<Record<string, PossibleActions>>({})

  const currentActions = computed(() => {
    const actions: Record<string, PossibleActions> = {}

    licenseTable.value?.licences.forEach((license) => {
      const appInformation = applicationInformation.value[license.software_id]

      if (manualChangedActions.value[license._id!]) {
        actions[license._id!] = manualChangedActions.value[license._id!]
      } else if (appInformation?.implements_delete_user_method) {
        actions[license._id!] = 'delete'
      } else if (appInformation?.implements_disable_licence_method) {
        actions[license._id!] = 'deactivate'
      } else {
        actions[license._id!] = 'none'
      }
    })

    return actions
  })

  // Misc
  const allLicensesSelected = computed(() => {
    return (
      licenseTable.value?.selection?.length ===
      licenseTable.value?.licences.length
    )
  })

  const allLicenseDeletionsSuccessful = computed(() => {
    return (
      Object.values(results.value).every(
        (result) => result.status === 'fulfilled'
      ) && personDeleted.value
    )
  })

  // ############################
  // ######### Filter ###########
  // ############################

  const filter = ref<AccountFilters>({})

  watch(
    () => props.person._id,
    () => {
      filter.value.accountId = props.person._id
    },
    { immediate: true }
  )

  defineExpose({
    licenseTable,
    excecute,
    createRequestBody,
    allLicenseDeletionsSuccessful,
    allLicensesSelected,
    results,
    deletePeopleLoading,
    personDeleted,
  })
</script>
